import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { FUENTE, useTema } from './tema';
import SelectorDePersona from './SelectorDePersona';
import { interpretarFirmante, esDeFuera } from './firmanteDeFuera';
import { personasDelDirectorio } from '../../services/planner';
import { pdfDelDocumentoEnBase64 } from '../../services/actaPublicada';
import {
  listarFirmasDeMinuta, mandarAFirmar, cancelarFirmas, reintentarAvisoDeFirma
} from '../../services/minutasV2Service';
import { cotejar } from './cotejarFirmantes';
import { aQuienProponer, sinRepetir } from './aQuienProponer';
import VisorDePdf, { dibujarPrimeraHoja } from './VisorDePdf';
import { enTodasLasHojas, solapados, sinRecuadro } from './geometriaDeFirmas';

/**
 * EnviarAFirmas — llevar el acta a la ronda de aprobaciones.
 *
 * Lo que gobierna esta pantalla es lo mismo que la de Planner, y por eso se le
 * parece: **mandar a firmar no se deshace**. A cada firmante le llega una
 * solicitud a su correo, y no hay un botón para retirarla. Así que antes de
 * mandar nada se enseña qué se manda, a quién, en qué orden, y se deja
 * cambiarlo.
 *
 * ── Quién pide las firmas ────────────────────────────────────────────────
 *
 * Esta aplicación, desde el buzón que se configura en Configuración → Correo.
 * Antes lo hacía Power Automate; se dejó de usar porque el disparador HTTP y
 * la llamada de vuelta son conectores de pago, y porque obligaba a montar un
 * flujo a mano por cada despliegue.
 *
 * Esto tiene una consecuencia que se ve en la pantalla: si un correo no sale,
 * el diálogo NO se cierra. Lo dice y deja reintentar. En una ronda de firmas
 * el correo no acompaña al proceso, es el proceso: un aviso que falla en
 * silencio deja el acta parada para siempre sin que nada lo delate.
 *
 * ── Por qué hace falta el documento publicado ────────────────────────────
 *
 * Porque lo que se firma es un documento, no una pantalla, y ese documento
 * viaja ADJUNTO en el correo de cada firmante. Aquí se convierte el .docx de
 * OneDrive a PDF —lo hace Office, con el token de quien manda— y se sube con
 * la ronda. El porqué del PDF, y de que se congele al abrir la ronda en vez de
 * enlazar al archivo vivo, está en `sql-migrations/v2-pdf-de-la-ronda.sql`.
 *
 * Por eso, si el acta todavía no está subida, el botón de mandar no se ofrece
 * y se dice por qué.
 */

/**
 * ── Por que dos columnas ─────────────────────────────────────────────────
 *
 * Mandar un acta a firmar son dos preguntas, y antes estaban mezcladas en una
 * sola lista vertical de la que habia que acordarse:
 *
 *   · QUE se manda   -> la columna de la izquierda, con el documento a la vista.
 *   · A QUIEN y COMO -> la de la derecha.
 *
 * Teniendo el documento delante mientras se elige a la gente, deja de hacer
 * falta abrirlo aparte para comprobar que es el que toca. Es la razon de que la
 * miniatura este ahi y no detras de un boton.
 *
 * En pantallas estrechas se apilan, y primero la del documento: es lo que
 * contesta «¿es este?».
 */
const estilos = (C) => ({
  fondo: {
    position: 'fixed', inset: 0, background: 'rgba(3,7,18,.62)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 100, padding: 16
  },
  caja: {
    width: 'min(1060px, 100%)', maxHeight: '92vh', display: 'flex', flexDirection: 'column',
    background: C.panel, border: `1px solid ${C.borde}`, borderRadius: 18,
    boxShadow: '0 22px 60px rgba(0,0,0,.5)', fontFamily: FUENTE, color: C.texto,
    overflow: 'hidden'
  },

  // ── Cabecera ────────────────────────────────────────────────────────
  cabecera: {
    display: 'flex', alignItems: 'flex-start', gap: 14,
    padding: '20px 22px 16px', borderBottom: `1px solid ${C.borde}`
  },
  emblema: {
    width: 46, height: 46, borderRadius: 13, flexShrink: 0,
    background: C.acentoSuave, color: C.acento,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 21
  },
  titulo: {
    margin: 0, fontSize: 23, fontWeight: 700, color: C.textoFuerte, letterSpacing: '-.01em'
  },
  bajada: { margin: '5px 0 0', fontSize: 13.5, color: C.suave, lineHeight: 1.55, maxWidth: 620 },
  cerrar: {
    marginLeft: 'auto', width: 34, height: 34, borderRadius: 9, flexShrink: 0,
    background: 'transparent', border: 'none', color: C.suave,
    fontSize: 20, cursor: 'pointer', lineHeight: 1, fontFamily: FUENTE
  },

  // ── Las dos columnas ────────────────────────────────────────────────
  cuerpo: {
    display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: 18, padding: 18, overflow: 'auto', flex: 1, alignContent: 'start'
  },
  columna: { display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 },

  panelDoc: {
    background: C.panelAlto, border: `1px solid ${C.borde}`, borderRadius: 14,
    padding: 14, display: 'flex', flexDirection: 'column', gap: 12
  },
  filaTitulo: { display: 'flex', alignItems: 'center', gap: 10 },
  rotuloFuerte: { fontSize: 15.5, fontWeight: 650, color: C.textoFuerte, flex: 1 },

  // La miniatura de la primera hoja. Con fondo BLANCO aunque el tema sea
  // oscuro: un documento es blanco, y pintarlo en gris lo hace parecer apagado
  // o a medio cargar.
  miniatura: {
    position: 'relative', borderRadius: 10, overflow: 'hidden',
    border: `1px solid ${C.borde}`, background: '#ffffff',
    minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  lienzoMini: { display: 'block', width: '100%', height: 'auto' },
  miniVacia: {
    padding: '38px 18px', textAlign: 'center', fontSize: 12.5, color: '#64748b', lineHeight: 1.6
  },

  tarjetaArchivo: {
    display: 'flex', alignItems: 'center', gap: 11,
    background: C.panel, border: `1px solid ${C.borde}`, borderRadius: 11, padding: '10px 12px'
  },
  iconoPdf: {
    width: 38, height: 38, borderRadius: 9, flexShrink: 0,
    background: 'rgba(185,28,28,.14)', color: '#ef4444',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 10.5, fontWeight: 700, letterSpacing: '.02em'
  },
  nombreArchivo: { fontSize: 13.5, fontWeight: 600, color: C.texto, wordBreak: 'break-word' },
  pesoArchivo: { fontSize: 12, color: C.tenue, marginTop: 2 },
  insignia: (listo) => ({
    marginLeft: 'auto', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5,
    fontSize: 12, fontWeight: 600, padding: '5px 9px', borderRadius: 999,
    background: listo ? 'rgba(16,185,129,.14)' : C.panelAlto,
    color: listo ? C.exito : C.tenue
  }),

  // ── Como firman ─────────────────────────────────────────────────────
  rotulo: { fontSize: 15.5, fontWeight: 650, color: C.textoFuerte, margin: '2px 0 0' },
  modos: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  modo: (activo) => ({
    textAlign: 'left', padding: '13px 14px', borderRadius: 13, cursor: 'pointer',
    border: `1.5px solid ${activo ? C.acento : C.borde}`,
    background: activo ? C.acentoSuave : C.panel,
    color: C.texto, fontFamily: FUENTE, display: 'flex', flexDirection: 'column', gap: 5
  }),
  modoCabeza: { display: 'flex', alignItems: 'center', gap: 8 },
  modoIcono: { fontSize: 17, color: C.suave },
  // El circulo de seleccion. Sin el, dos tarjetas con distinto borde se leen
  // como «una esta resaltada» y no como «una esta elegida».
  pelota: (activo) => ({
    marginLeft: 'auto', width: 17, height: 17, borderRadius: '50%', flexShrink: 0,
    border: `2px solid ${activo ? C.acento : C.borde}`,
    background: activo ? C.acento : 'transparent',
    boxShadow: activo ? `inset 0 0 0 3px ${C.panel}` : 'none'
  }),
  modoTitulo: { fontSize: 14.5, fontWeight: 650, color: C.textoFuerte },
  modoPista: { fontSize: 12.5, color: C.suave, lineHeight: 1.5 },

  // ── Quien firma ─────────────────────────────────────────────────────
  lista: { display: 'flex', flexDirection: 'column' },
  // Cada firmante con su numero, y un hilo que los une. Eso es lo que
  // convierte una lista en una SECUENCIA, que es lo que de verdad pasa en modo
  // «uno tras otro». En paralelo el hilo se quita: ahi no hay orden que seguir,
  // y dibujarlo seria decir algo falso.
  filaFirmante: (enVuelo) => ({
    display: 'flex', alignItems: 'stretch', gap: 10, opacity: enVuelo ? .4 : 1
  }),
  carril: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: 27 },
  numero: (activo) => ({
    width: 27, height: 27, borderRadius: '50%', flexShrink: 0,
    background: activo ? C.acento : C.panelAlto,
    color: activo ? C.acentoTexto : C.suave,
    border: activo ? 'none' : `1px solid ${C.borde}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12.5, fontWeight: 700, marginTop: 13
  }),
  hilo: { width: 2, flex: 1, background: C.borde, minHeight: 6 },
  tarjetaFirmante: (vacia) => ({
    flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 11px', borderRadius: 12, margin: '5px 0',
    border: `1px ${vacia ? 'dashed' : 'solid'} ${C.borde}`,
    background: vacia ? 'transparent' : C.panelAlto
  }),
  avatar: {
    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
    background: C.acentoSuave, color: C.acento,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12.5, fontWeight: 700
  },
  nombreFirmante: { fontSize: 14, fontWeight: 600, color: C.textoFuerte, wordBreak: 'break-word' },
  correoFirmante: { fontSize: 12.5, color: C.suave, wordBreak: 'break-all', marginTop: 1 },
  chipFuera: {
    display: 'inline-block', marginLeft: 7, fontSize: 10.5, fontWeight: 700,
    padding: '1px 6px', borderRadius: 999, background: C.panel,
    border: `1px solid ${C.borde}`, color: C.suave, verticalAlign: 'middle'
  },
  iconoFila: {
    width: 30, height: 30, borderRadius: 8, flexShrink: 0,
    background: 'transparent', border: 'none', color: C.tenue,
    fontSize: 14, cursor: 'pointer', fontFamily: FUENTE, lineHeight: 1
  },
  anadir: {
    width: '100%', padding: '11px 12px', borderRadius: 12, cursor: 'pointer',
    border: `1px dashed ${C.borde}`, background: C.acentoSuave, color: C.acento,
    fontSize: 13.5, fontWeight: 600, fontFamily: FUENTE, marginTop: 6
  },
  vacia: { fontSize: 13, color: C.tenue, padding: '8px 2px' },

  // ── Mensaje ─────────────────────────────────────────────────────────
  area: {
    width: '100%', minHeight: 92, resize: 'vertical', padding: '11px 12px',
    borderRadius: 12, border: `1px solid ${C.borde}`, background: C.panel,
    color: C.texto, fontSize: 13.5, fontFamily: FUENTE, lineHeight: 1.55,
    boxSizing: 'border-box'
  },
  contador: (cerca) => ({
    fontSize: 11.5, color: cerca ? C.atencion : C.tenue, textAlign: 'right', marginTop: 4
  }),

  // ── Pie ─────────────────────────────────────────────────────────────
  pie: {
    display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end',
    padding: '14px 20px', borderTop: `1px solid ${C.borde}`, background: C.panelAlto
  },
  botonFlojo: {
    padding: '10px 20px', borderRadius: 11, cursor: 'pointer',
    background: C.panel, color: C.texto, border: `1px solid ${C.borde}`,
    fontSize: 14, fontWeight: 600, fontFamily: FUENTE
  },
  botonFuerte: (apagado) => ({
    padding: '10px 22px', borderRadius: 11, border: 'none',
    cursor: apagado ? 'not-allowed' : 'pointer', opacity: apagado ? 0.5 : 1,
    background: C.acento, color: C.acentoTexto,
    fontSize: 14, fontWeight: 650, fontFamily: FUENTE,
    display: 'inline-flex', alignItems: 'center', gap: 8
  }),
  botonChico: {
    padding: '7px 12px', borderRadius: 9, cursor: 'pointer',
    background: C.panel, color: C.texto, border: `1px solid ${C.borde}`,
    fontSize: 12.5, fontWeight: 600, fontFamily: FUENTE
  },

  // ── Lo que ya se mando, y los avisos ────────────────────────────────
  bloque: {
    background: C.panelAlto, border: `1px solid ${C.borde}`, borderRadius: 14, padding: 14
  },
  historial: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 },
  estado: (malo) => ({ fontSize: 12.5, color: malo ? C.error : C.tenue, lineHeight: 1.5, flex: 1 }),
  punto: (color) => ({
    display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
    background: color, marginRight: 7, verticalAlign: 'middle'
  }),
  // Un aviso que no para nada. En tono de atencion y no de error a proposito:
  // lo que cuenta puede ser justo lo que se queria —firma alguien que no fue a
  // la reunion— y pintarlo en rojo haria dudar de una decision correcta.
  recuadro: {
    padding: '10px 13px', borderRadius: 11, color: C.texto, fontSize: 12.5, lineHeight: 1.6,
    background: C.panelAlto, border: `1px solid ${C.borde}`,
    borderLeft: `3px solid ${C.acento}`
  },
  ojo: { fontSize: 12, color: C.tenue, lineHeight: 1.5 },

  // ── La ventana de colocar las firmas ────────────────────────────────
  //
  // Va aparte y encima de esta, no dentro: dentro tendria que caber en una
  // columna, y señalar un punto concreto de una hoja por una rendija no es
  // incomodo, es imposible.
  fondoVisor: {
    position: 'fixed', inset: 0, background: 'rgba(3,7,18,.78)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 120, padding: 16
  },
  cajaVisor: {
    width: 'min(1180px, 100%)', height: '94vh', display: 'flex', flexDirection: 'column',
    background: C.panel, border: `1px solid ${C.borde}`, borderRadius: 14,
    boxShadow: '0 22px 60px rgba(0,0,0,.5)', fontFamily: FUENTE, color: C.texto,
    overflow: 'hidden'
  },
  cuerpoVisor: { padding: '12px 18px 18px', overflow: 'auto', flex: 1 }
});

/**
 * Lo que cabe en el recado para los firmantes.
 *
 * Es un recado, no un anexo: lo que no quepa en 500 caracteres pertenece al
 * acta, que es el documento que se firma y el que queda. El mismo tope esta en
 * la columna de la base de datos, asi que el contador de la pantalla dice la
 * verdad y no se recorta nada al guardar.
 */
const MAX_MENSAJE = 500;

/** Las iniciales para el circulo de cada firmante. */
function iniciales(nombre, correo) {
  const limpio = String(nombre || '').trim();
  if (limpio) {
    const partes = limpio.split(/\s+/).filter(Boolean);
    const dos = partes.length > 1 ? [partes[0], partes[partes.length - 1]] : [partes[0]];
    return dos.map(p => p[0]).join('').toUpperCase().slice(0, 2);
  }
  // Sin nombre —un correo de fuera recien escrito— se usa el correo, que es lo
  // unico que hay. Un circulo vacio no distingue a nadie de nadie.
  return String(correo || '?').trim().slice(0, 2).toUpperCase();
}

/** El peso de un PDF guardado en base64, en lo que se lee de un vistazo. */
function pesoDeBase64(base64) {
  if (!base64) return null;
  // base64 abulta un tercio mas que los bytes de verdad.
  const bytes = Math.floor(String(base64).length * 3 / 4);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const COLOR_ESTADO = (C) => ({
  'en curso': C.atencion,
  aprobada: C.acentoClaro,
  rechazada: C.error,
  cancelada: C.tenue
});

/**
 * @param {Object} props
 * @param {number} props.minuteId
 * @param {string} props.userId
 * @param {Function} props.tokenGraph
 * @param {string} props.titulo
 * @param {string|null} props.folio
 * @param {Object|null} props.documento - el acta subida: { driveId, itemId, webUrl, nombre }
 * @param {Array} props.sugeridos - a quién proponer, de los asistentes del acta
 * @param {Function} props.onCerrar
 */
const EnviarAFirmas = ({
  minuteId, userId, tokenGraph, titulo = '', folio = null,
  // Quien la manda, para poder avisarle si alguien la rechaza. Su correo llega
  // como prop y no se deduce del `userId`: ese es el identificador de cuenta de
  // Azure AD, no una direccion.
  miCorreo = '', miNombre = '',
  // Lo que vera quien firme sin abrir el Word. Se guarda con la ronda.
  resumen = null,
  documento = null, sugeridos = [], onCerrar
}) => {
  const { C } = useTema();
  const S = useMemo(() => estilos(C), [C]);
  const colores = useMemo(() => COLOR_ESTADO(C), [C]);

  const [gente, setGente] = useState([]);
  // Hay que esperar a las dos fuentes antes de proponer a nadie; ver el efecto
  // de siembra mas abajo.
  const [directorioListo, setDirectorioListo] = useState(false);
  const [enviosListos, setEnviosListos] = useState(false);
  const [elegidos, setElegidos] = useState([]);
  const [modo, setModo] = useState('secuencial');
  const [envios, setEnvios] = useState([]);
  // Sin buzón de salida no sale ningún correo, y el correo ES la ronda: si no
  // llega, quien tiene que firmar no se entera. Se comprueba al abrir para
  // decirlo antes de que alguien elija seis personas, no al pulsar.
  const [hayBuzon, setHayBuzon] = useState(true);
  const [mandando, setMandando] = useState(false);
  // En qué paso va el envío. Convertir el acta a PDF tarda unos segundos —lo
  // hace Office, no el navegador—, y un botón que solo dice «Mandando…» durante
  // ese rato hace pensar que se ha quedado colgado y que hay que volver a pulsar.
  const [paso, setPaso] = useState('');
  // Ver el PDF exacto que se va a adjuntar, ANTES de mandarlo.
  //
  // Mandar a firmar no se deshace: en cuanto sale, cinco personas tienen en su
  // bandeja el documento que sea. Y el PDF no se fabrica aquí —lo convierte
  // Office desde el .docx de OneDrive—, así que hasta ahora la única manera de
  // saber qué se había mandado era preguntárselo a quien lo recibió.
  const [viendo, setViendo] = useState(false);
  const [errorVista, setErrorVista] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [malo, setMalo] = useState(false);

  // ── Dónde firma cada quien, señalado sobre el documento ───────────────
  //
  // `[{correo, pagina, x, y, ancho, alto}]`, en fracciones de 0 a 1 con el
  // origen arriba; el contrato está en `geometriaDeFirmas.js`. Van por correo y
  // no por posición en la lista porque el servidor renumera a la gente al
  // limpiar duplicados, y por índice las firmas acabarían cambiadas de sitio.
  const [recuadros, setRecuadros] = useState([]);

  // El recado para los firmantes. Viaja con la ronda y sale en SU correo; el
  // porque de que sea uno solo para todos esta en la migracion
  // `v2-mensaje-de-la-ronda.sql`.
  const [mensaje, setMensaje] = useState('');

  // Sin acta subida no hay nada que firmar: lo que se firma es el documento, y
  // a cada firmante le llega en PDF. Se calcula aqui arriba porque lo miran
  // varios efectos antes de pintarse nada.
  const sinDocumento = !documento || !documento.webUrl;
  const [colocando, setColocando] = useState(false);
  // De quién es el recuadro que se va a poner con el siguiente clic.
  const [turnoDe, setTurnoDe] = useState(null);
  const [abriendo, setAbriendo] = useState(false);

  // ── El PDF, pedido UNA vez ────────────────────────────────────────────
  //
  // Esto es importante y no es una optimización: las coordenadas se colocan
  // SOBRE un PDF concreto. Si al enviar se pidiera otra conversión, podría no
  // ser la misma —el acta se edita mientras esta ventana está abierta— y los
  // recuadros caerían sobre un documento con el texto corrido. Las firmas
  // saldrían descolocadas sin que nada lo señalara.
  //
  // Es la misma razón por la que la vista previa de abajo abre los bytes que
  // acaban de volver en vez de pedirlos de nuevo.
  const [pdfEnMano, setPdfEnMano] = useState(null);
  // Se trae nada mas abrir, en segundo plano, para pintar la miniatura. No
  // bloquea: la columna de la derecha se puede usar mientras. Y de paso deja
  // listos los dos pasos que venian despues —señalar las firmas y enviar—, que
  // antes esperaban esta misma conversion.
  const [trayendoPdf, setTrayendoPdf] = useState(false);
  const lienzoMini = useRef(null);
  // Cuántas hojas tiene. Lo dice el visor al abrirlo: aquí no se puede saber
  // sin leer el PDF, y leerlo dos veces para contar hojas sería absurdo.
  const [hojas, setHojas] = useState(0);

  // Arrastrar para cambiar el orden, como en las tablas del acta.
  const [enVuelo, setEnVuelo] = useState(null);
  const [destino, setDestino] = useState(null);

  // ── El directorio ─────────────────────────────────────────────────────
  useEffect(() => {
    let vigente = true;
    (async () => {
      try {
        const lista = await personasDelDirectorio(await tokenGraph());
        if (vigente) { setGente(lista); setDirectorioListo(true); }
      } catch {
        // Sin directorio no se puede elegir a nadie, y eso sí hay que decirlo:
        // es la diferencia entre «no encuentro a Ana» y «no hay lista».
        if (vigente) {
          setGente([]);
          setDirectorioListo(true);
          setAviso('No se pudo leer el directorio de la empresa.');
          setMalo(true);
        }
      }
    })();
    return () => { vigente = false; };
  }, [tokenGraph]);

  // ── Lo que ya se mandó ────────────────────────────────────────────────
  const refrescar = useCallback(async () => {
    try {
      const { rounds, canSend } = await listarFirmasDeMinuta(minuteId, userId);
      setEnvios(rounds || []);
      setHayBuzon(canSend !== false);
    } catch {
      setEnvios([]);
    } finally {
      setEnviosListos(true);
    }
  }, [minuteId, userId]);

  useEffect(() => { refrescar(); }, [refrescar]);

  // ── A quién se propone al abrir ───────────────────────────────────────
  //
  // Dos fuentes, y una manda sobre la otra:
  //
  //   1. A QUIÉN SE LE MANDÓ LA ÚLTIMA VEZ. Es una decisión que alguien ya
  //      tomó para ESTA acta, así que gana. Antes no se usaba, y por eso había
  //      que volver a elegir a todo el mundo cada vez que se abría la ventana
  //      —incluidos los correos de fuera, que no están en el directorio y no
  //      los recupera nada—.
  //
  //   2. Los asistentes del acta, si nunca se ha mandado. Quien firma suele ser
  //      quien estuvo, y escribir otra vez seis nombres que la aplicación ya
  //      tiene delante es trabajo que no hacía falta.
  //
  // Se espera a las DOS fuentes antes de decidir. Si se sembrara en cuanto
  // llega una, la otra ya no podría corregirlo: con `elegidos` lleno, el efecto
  // no vuelve a entrar.
  const sembrado = useRef(false);

  useEffect(() => {
    if (sembrado.current || elegidos.length > 0) return;
    if (!directorioListo || !enviosListos) return;

    const { firmantes, modo: suModo } = aQuienProponer({
      ultimoEnvio: envios[0] || null,
      directorio: gente,
      sugeridos
    });
    if (firmantes.length === 0) return;

    sembrado.current = true;
    setElegidos(firmantes);
    if (suModo) setModo(suModo);
    // `elegidos` queda fuera: entra por `sembrado` y por el corte de arriba.
    // Incluirlo volveria a sembrar cada vez que se añade o se quita a alguien.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directorioListo, enviosListos, gente, envios, sugeridos]);

  // Lo que no cuadra entre quien firma y quien asistio. Se calcula en cada
  // pintado porque las dos listas se editan aqui mismo: el aviso tiene que
  // aparecer y desaparecer segun se añaden y se quitan firmantes.
  const desencajados = cotejar(elegidos, sugeridos);

  const añadir = useCallback((persona) => {
    if (!persona || !persona.correo) return;
    setElegidos(actuales => sinRepetir([...actuales, persona]));
  }, []);

  const quitar = useCallback((correo) => {
    setElegidos(actuales => actuales.filter(p => p.correo !== correo));
  }, []);

  const mover = useCallback((desde, hasta) => {
    if (desde === hasta || hasta < 0) return;
    setElegidos(actuales => {
      if (hasta >= actuales.length) return actuales;
      const nuevos = actuales.slice();
      const [quien] = nuevos.splice(desde, 1);
      nuevos.splice(hasta, 0, quien);
      return nuevos;
    });
  }, []);

  // ── Parar una ronda en marcha ────────────────────────────────────────
  //
  // Hace falta para poder corregir el acta sin que nadie firme una version que
  // se va a tirar. Al cancelar, los enlaces que ya salieron dejan de valer: la
  // pagina de firma lo dice en vez de dejar firmar algo cerrado.
  const cancelar = useCallback(async () => {
    setMandando(true);
    setAviso(null);
    setMalo(false);
    try {
      await cancelarFirmas(minuteId, userId);
      await refrescar();
      setAviso('Ronda cancelada. Los enlaces que ya salieron dejan de valer.');
    } catch (e) {
      setAviso(e.message || 'No se pudo cancelar.');
      setMalo(true);
    } finally {
      setMandando(false);
    }
  }, [minuteId, userId, refrescar]);

  // Volver a mandarle el correo a quien no lo recibio. Sin esto, una ronda
  // parada porque un aviso no salio no tiene arreglo desde la pantalla.
  const reintentar = useCallback(async (roundId) => {
    setMandando(true);
    setAviso(null);
    setMalo(false);
    try {
      const r = await reintentarAvisoDeFirma(roundId, userId);
      await refrescar();
      if (r.failures && r.failures.length > 0) {
        setAviso(`Sigue sin poder avisarse a ${r.failures.map(f => f.correo).join(', ')}: ${r.failures[0].motivo}`);
        setMalo(true);
      } else {
        setAviso(`Avisado ${(r.notified || []).join(', ')}.`);
      }
    } catch (e) {
      setAviso(e.message || 'No se pudo reintentar.');
      setMalo(true);
    } finally {
      setMandando(false);
    }
  }, [userId, refrescar]);

  const verElPdf = useCallback(async () => {
    if (viendo || !documento) return;
    setViendo(true);
    setErrorVista(null);
    try {
      const pdf = await pdfDelDocumentoEnBase64(await tokenGraph(), documento);

      // Se abre desde los bytes que acaban de volver, no pidiéndoselo otra vez
      // a Graph: si se pidiera dos veces, lo que se mira y lo que se manda
      // serían dos conversiones distintas y esto dejaría de probar nada.
      const bytes = Uint8Array.from(atob(pdf.base64), c => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      // Un minuto de sobra para que la pestaña lo cargue; después se suelta la
      // memoria, que son varios megas.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setErrorVista(e.message || 'No se pudo convertir el acta.');
    } finally {
      setViendo(false);
    }
  }, [viendo, documento, tokenGraph]);

  /**
   * Trae el acta en PDF y abre el panel para señalar dónde va cada firma.
   *
   * Se pide una sola vez y se guarda: ver `pdfEnMano`.
   */
  const colocarFirmas = useCallback(async () => {
    if (abriendo) return;
    if (colocando) { setColocando(false); return; }

    setAbriendo(true);
    setErrorVista(null);
    try {
      const pdf = pdfEnMano || await pdfDelDocumentoEnBase64(await tokenGraph(), documento);
      if (!pdfEnMano) setPdfEnMano(pdf);
      setColocando(true);
      // Por defecto le toca al primero de la lista: en secuencial es quien
      // firma antes, y así se puede empezar a colocar sin elegir nada.
      setTurnoDe((actual) => actual || (elegidos[0] && elegidos[0].correo) || null);
    } catch (e) {
      setErrorVista(e.message || 'No se pudo convertir el acta para colocar las firmas.');
    } finally {
      setAbriendo(false);
    }
  }, [abriendo, colocando, pdfEnMano, documento, tokenGraph, elegidos]);

  // Los bytes para el visor. Se derivan del base64 una sola vez: convertirlos
  // en cada pintado haría varios megas de trabajo a cada hoja que se pasa.
  const bytesDelPdf = useMemo(() => {
    if (!pdfEnMano || !pdfEnMano.base64) return null;
    const crudo = atob(pdfEnMano.base64);
    const bytes = new Uint8Array(crudo.length);
    for (let i = 0; i < crudo.length; i++) bytes[i] = crudo.charCodeAt(i);
    return bytes;
  }, [pdfEnMano]);

  const ponerRecuadro = useCallback((pagina, caja) => {
    if (!turnoDe) return;
    setRecuadros((previos) => [...previos, { ...caja, pagina, correo: turnoDe }]);
  }, [turnoDe]);

  const ponerEnTodas = useCallback(() => {
    // Toma el último recuadro de esta persona como molde y lo repite. Hace
    // falta uno puesto: sin él no hay nada que repetir, y adivinar un sitio
    // «razonable» pondría la firma encima del texto.
    const suyos = recuadros.filter(c => c.correo === turnoDe);
    const molde = suyos[suyos.length - 1];
    if (!molde) {
      setAviso('Coloca primero una firma en una hoja; desde ahí se repite en todas.');
      setMalo(false);
      return;
    }
    if (!hojas) {
      setAviso('Espera a que el documento acabe de abrirse.');
      setMalo(false);
      return;
    }
    setRecuadros((previos) => [
      ...previos.filter(c => c.correo !== turnoDe),
      ...enTodasLasHojas({ x: molde.x, y: molde.y, ancho: molde.ancho, alto: molde.alto, correo: turnoDe }, hojas)
    ]);
  }, [recuadros, turnoDe, hojas]);

  const quitarRecuadro = useCallback((indice) => {
    setRecuadros((previos) => previos.filter((_, i) => i !== indice));
  }, []);

  const moverRecuadro = useCallback((indice, caja) => {
    setRecuadros((previos) => previos.map((c, i) => (i === indice ? { ...c, ...caja } : c)));
  }, []);

  // Si alguien deja de estar en la lista, sus recuadros no pueden quedarse: el
  // servidor los descartaría de todos modos, pero aquí seguirían dibujados y
  // quien manda creería que esa firma está colocada.
  useEffect(() => {
    const vigentes = new Set(elegidos.map(p => String(p.correo || '').toLowerCase()));
    setRecuadros((previos) => {
      const quedan = previos.filter(c => vigentes.has(String(c.correo || '').toLowerCase()));
      return quedan.length === previos.length ? previos : quedan;
    });
  }, [elegidos]);

  // ── El acta en PDF, traida en segundo plano al abrir ─────────────────
  //
  // No bloquea nada: la columna de la derecha —a quien y como— se puede usar
  // mientras se convierte. Y lo que se trae aqui sirve para las tres cosas que
  // venian despues y esperaban esta misma conversion: la miniatura, señalar
  // donde firma cada quien, y el propio envio.
  //
  // Convertir lo hace Office, no el navegador, y tarda unos segundos. Por eso
  // se empieza cuanto antes en vez de al pulsar un boton.
  useEffect(() => {
    if (pdfEnMano || trayendoPdf || sinDocumento) return;
    let vigente = true;
    setTrayendoPdf(true);
    (async () => {
      try {
        const pdf = await pdfDelDocumentoEnBase64(await tokenGraph(), documento);
        if (vigente) setPdfEnMano(pdf);
      } catch (e) {
        // Que no haya miniatura no impide mandar a firmar: el envio vuelve a
        // intentar la conversion por su cuenta y dice lo suyo si falla.
        if (vigente) setErrorVista(e.message || 'No se pudo preparar la vista del documento.');
      } finally {
        if (vigente) setTrayendoPdf(false);
      }
    })();
    return () => { vigente = false; };
  }, [pdfEnMano, trayendoPdf, sinDocumento, documento, tokenGraph]);

  // La primera hoja, dibujada en la miniatura.
  useEffect(() => {
    if (!bytesDelPdf || !lienzoMini.current) return;
    let vigente = true;
    dibujarPrimeraHoja(bytesDelPdf, lienzoMini.current, 420).catch(() => {
      // Una miniatura que no sale no es motivo para enseñar un error: el
      // documento se puede ver entero con «Ver PDF», y el envio no depende
      // de esto.
      if (vigente) { /* se queda el hueco */ }
    });
    return () => { vigente = false; };
  }, [bytesDelPdf]);

  const enviar = useCallback(async () => {
    if (elegidos.length === 0 || mandando) return;
    setMandando(true);
    setAviso(null);
    setMalo(false);
    try {
      // Primero el PDF, antes de abrir la ronda.
      //
      // Si esto falla no se manda nada, a propósito. La alternativa —abrir la
      // ronda y mandar los correos con un enlace al Word— dejaría a unos
      // firmantes con el acta adjunta y a otros con un enlace, según el día,
      // sin que nadie lo hubiera decidido. Aquí falla del todo y se puede
      // volver a intentar; el acta sigue sin mandarse a nadie.
      setPaso('Preparando el acta en PDF…');
      // El que ya se trajo para colocar las firmas, si se colocaron. Pedir otra
      // conversión aquí podría dar un documento distinto —el acta se edita
      // mientras esta ventana está abierta— y los recuadros caerían sobre otro
      // texto. Ver `pdfEnMano`.
      const pdf = pdfEnMano || await pdfDelDocumentoEnBase64(await tokenGraph(), documento);

      setPaso('Avisando a quien le toca…');

      const r = await mandarAFirmar(minuteId, userId, {
        mode: modo,
        title: titulo,
        folio,
        documentUrl: (documento && documento.webUrl) || null,
        // El acta que viaja adjunta. Se guarda con la ronda: lo que firme el
        // tercero tiene que ser lo mismo que leyó el primero.
        documentPdf: pdf,
        signers: elegidos.map(p => ({ nombre: p.nombre, correo: p.correo, puesto: p.puesto })),
        // Para poder avisarle si alguien la rechaza. El `userId` no sirve: es
        // el identificador de cuenta de Azure AD, no una dirección.
        requestedByEmail: miCorreo || null,
        requestedByName: miNombre || null,
        summary: resumen,
        message: mensaje.trim() || null,
        // Dónde va la firma de cada quien. Si no se colocó ninguna, va vacío y
        // el servidor hereda los de la ronda anterior —un reenvío es sobre el
        // mismo documento— o se queda sin recuadros, y entonces la firma se
        // estampa donde siempre: en la columna «Firma» de la tabla de
        // asistentes del .docx. Las dos cosas siguen funcionando.
        fields: recuadros
      });

      // Un correo que no sale deja a alguien sin saber que tiene que firmar, y
      // la ronda parada sin que nada lo delate. Así que si falló alguno, la
      // ventana se queda abierta diciéndolo en vez de cerrarse como si todo
      // hubiera ido bien.
      if (r && Array.isArray(r.failures) && r.failures.length > 0) {
        setAviso(
          `La ronda se abrió, pero no se pudo avisar a ${r.failures.map(f => f.correo).join(', ')}: `
          + r.failures[0].motivo
        );
        setMalo(true);
        setMandando(false);
        setPaso('');
        refrescar();
        return;
      }
      onCerrar(true);
    } catch (e) {
      // Se distingue el fallo de la conversión del fallo del envío: «no se pudo
      // mandar a firmar» cuando lo que pasó es que Office no convirtió el
      // documento manda a buscar el problema donde no está.
      setAviso(e.name === 'OneDriveError'
        ? `No se pudo preparar el acta en PDF para mandarla: ${e.message} `
          + 'No se ha avisado a nadie; vuelve a intentarlo.'
        : (e.message || 'No se pudo mandar a firmar.'));
      setMalo(true);
      setMandando(false);
      setPaso('');
      refrescar();
    }
  }, [elegidos, mandando, minuteId, userId, modo, titulo, folio, documento, onCerrar,
    refrescar, miCorreo, miNombre, resumen, tokenGraph, pdfEnMano, recuadros, mensaje]);

  // ── Lo que sale mal al colocar y no se ve en pantalla ────────────────
  //
  // Las dos son avisos, no bloqueos. Mandar sin recuadros es legitimo —la firma
  // va entonces a la tabla del Word, como siempre— y dos recuadros juntos
  // pueden ser lo que alguien queria. Lo que no vale es que no se note.
  const faltanPorColocar = useMemo(
    () => (recuadros.length === 0 ? [] : sinRecuadro(elegidos, recuadros)),
    [elegidos, recuadros]
  );
  const sePisanRecuadros = useMemo(() => solapados(recuadros), [recuadros]);

  const pesoDelPdf = useMemo(
    () => (pdfEnMano ? pesoDeBase64(pdfEnMano.base64) : null),
    [pdfEnMano]
  );

  const esperando = envios.some(e => e.estado === 'en curso');

  return (
    <>
    <div style={S.fondo} onMouseDown={(e) => { if (e.target === e.currentTarget) onCerrar(false); }}>
      <div style={S.caja} role="dialog" aria-modal="true" aria-label="Enviar el acta a firmar">

        <div style={S.cabecera}>
          <div style={S.emblema} aria-hidden="true">➤</div>
          <div style={{ minWidth: 0 }}>
            <h3 style={S.titulo}>Enviar a firmar</h3>
            <p style={S.bajada}>
              Cada firmante recibirá un correo con una solicitud de aprobación. El documento
              se adjunta en PDF y podrá firmarse desde cualquier dispositivo.
            </p>
          </div>
          <button style={S.cerrar} onClick={() => onCerrar(false)} aria-label="Cerrar">×</button>
        </div>

        <div style={S.cuerpo}>

          {/* ── Qué se manda ──────────────────────────────────────────── */}
          <div style={S.columna}>
            <div style={S.panelDoc}>
              <div style={S.filaTitulo}>
                <span style={S.rotuloFuerte}>Documento a firmar</span>
                {!sinDocumento && (
                  <button style={S.botonChico} onClick={verElPdf} disabled={viendo}>
                    {viendo ? 'Convirtiendo…' : '👁  Ver PDF'}
                  </button>
                )}
              </div>

              <div style={S.miniatura}>
                {bytesDelPdf
                  ? <canvas ref={lienzoMini} style={S.lienzoMini} />
                  : (
                    <div style={S.miniVacia}>
                      {sinDocumento
                        ? 'El acta todavía no está subida.'
                        : trayendoPdf
                          ? 'Convirtiendo el acta con Office…'
                          : errorVista || 'Preparando la vista del documento…'}
                    </div>
                  )}
              </div>

              <div style={S.tarjetaArchivo}>
                <div style={S.iconoPdf} aria-hidden="true">PDF</div>
                <div style={{ minWidth: 0 }}>
                  <div style={S.nombreArchivo}>
                    {(pdfEnMano && pdfEnMano.nombre) || `${folio || titulo || 'Acta'}.pdf`}
                  </div>
                  <div style={S.pesoArchivo}>
                    {pesoDelPdf ? `${pesoDelPdf} · PDF` : 'PDF'}
                  </div>
                </div>
                <span style={S.insignia(Boolean(bytesDelPdf))}>
                  {bytesDelPdf ? '✓ Listo para firmar' : '… preparando'}
                </span>
              </div>

              {/* Señalar dónde firma cada quien. Se hace ANTES de enviar: el
                  correo de cada persona ya sabe en qué hoja y en qué punto le
                  toca, y el PDF final se puede estampar ahí.

                  Es opcional. Sin recuadros, la firma sigue yendo a la columna
                  «Firma» de la tabla de asistentes del .docx, como siempre. */}
              {!sinDocumento && elegidos.length > 0 && (
                <button style={S.botonChico} onClick={colocarFirmas} disabled={abriendo}>
                  {abriendo
                    ? 'Convirtiendo el acta con Office…'
                    : recuadros.length > 0
                      ? `✍  Firmas colocadas: ${recuadros.length} — revisar`
                      : '✍  Señalar dónde firma cada quien'}
                </button>
              )}
            </div>

            {sinDocumento && (
              <div style={{ ...S.recuadro, borderLeftColor: C.atencion }}>
                El acta todavía no está subida, y lo que se firma es el documento: a cada
                firmante le llega en PDF. Espera a que termine de publicarse y vuelve a
                intentarlo.
              </div>
            )}
            {!hayBuzon && (
              <div style={{ ...S.recuadro, borderLeftColor: C.error }}>
                No hay buzón de salida configurado, así que no se puede avisar a nadie. Se pone
                una vez en Configuración → Correo.
              </div>
            )}

            {/* Lo que no cuadra entre quien firma y quien asistió. Avisa, no
                bloquea: puede ser justo lo que se quería. */}
            {desencajados.length > 0 && (
              <div style={S.recuadro}>
                {desencajados.join(' ')}
              </div>
            )}

            {/* ── La ronda que está viva ─────────────────────────────
                Aquí estaba el historial: las cuatro últimas rondas con su
                estado y su fecha. Se quitó porque no servía para lo que se
                hace en esta ventana —mandar a firmar— y lo que enseñaba era
                sobre todo ruido: «cancelada · ronda 10 · 0 de 1» cuatro veces
                seguidas no le dice nada a quien está eligiendo firmantes.

                Lo que SÍ se queda es la ronda en curso, que no es historia:
                es lo que está pasando ahora con esta acta, y es donde están
                las dos únicas cosas que se pueden hacer con ella —reintentar
                un aviso que no salió y cancelarla—. Sin esto, una ronda
                atascada no se podría parar desde ninguna parte. */}
            {(() => {
              const viva = envios.find(e => e.estado === 'en curso');
              if (!viva) return null;
              const firmadas = viva.firmantes.filter(f => f.estado === 'aprobada').length;
              // Quién no recibió su correo. Una ronda parada porque un aviso no
              // salió parece, desde fuera, una ronda que nadie ha atendido.
              const sinAvisar = viva.firmantes.filter(f => f.errorEnvio);
              return (
                <div style={S.bloque}>
                  <div style={S.rotulo}>Ya hay una ronda en curso</div>
                  <div style={{ marginTop: 6, fontSize: 12.5, color: C.suave, lineHeight: 1.6 }}>
                    <span style={S.punto(colores[viva.estado] || C.tenue)} />
                    Han firmado {firmadas} de {viva.firmantes.length}
                    {viva.modo === 'secuencial' ? ', en orden' : ', a la vez'}.
                    {' '}Si mandas otra, esta se cancela.
                  </div>
                  {sinAvisar.length > 0 && (
                    <div style={{ marginTop: 4, fontSize: 12.5, color: C.error }}>
                      No se pudo avisar a {sinAvisar.map(f => f.correo).join(', ')}.
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 9, flexWrap: 'wrap' }}>
                    {sinAvisar.length > 0 && (
                      <button style={S.botonChico} disabled={mandando}
                        onClick={() => reintentar(viva.id)}>
                        Reintentar el aviso
                      </button>
                    )}
                    <button style={S.botonChico} disabled={mandando} onClick={cancelar}>
                      Cancelar esta ronda
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* ── A quién y cómo ────────────────────────────────────────── */}
          <div style={S.columna}>

            <div style={S.rotulo}>¿Cómo quieren firmar?</div>
            <div style={S.modos}>
              <button style={S.modo(modo === 'secuencial')} onClick={() => setModo('secuencial')}>
                <span style={S.modoCabeza}>
                  <span style={S.modoIcono} aria-hidden="true">👤</span>
                  <span style={S.pelota(modo === 'secuencial')} />
                </span>
                <span style={S.modoTitulo}>Uno tras otro</span>
                <span style={S.modoPista}>
                  Cada persona firma cuando la anterior ha terminado. Si alguien rechaza,
                  se para ahí.
                </span>
              </button>
              <button style={S.modo(modo === 'paralelo')} onClick={() => setModo('paralelo')}>
                <span style={S.modoCabeza}>
                  <span style={S.modoIcono} aria-hidden="true">👥</span>
                  <span style={S.pelota(modo === 'paralelo')} />
                </span>
                <span style={S.modoTitulo}>Todos a la vez</span>
                <span style={S.modoPista}>
                  Todos reciben el documento al mismo tiempo. Más rápido, sin orden
                  jerárquico.
                </span>
              </button>
            </div>

            <div style={S.rotulo}>
              Quién firma{modo === 'secuencial' ? ', en este orden' : ''}
            </div>

            <div style={S.lista}>
              {elegidos.length === 0 && (
                <div style={S.vacia}>Todavía no has elegido a nadie.</div>
              )}

              {elegidos.map((quien, i) => (
                <div
                  key={quien.correo}
                  style={S.filaFirmante(enVuelo === i)}
                  draggable
                  onDragStart={(ev) => {
                    ev.dataTransfer.effectAllowed = 'move';
                    ev.dataTransfer.setData('text/plain', String(i));
                    setEnVuelo(i);
                  }}
                  onDragOver={(ev) => {
                    if (enVuelo === null) return;
                    ev.preventDefault();
                    if (destino !== i) setDestino(i);
                  }}
                  onDrop={(ev) => {
                    ev.preventDefault();
                    if (enVuelo !== null) mover(enVuelo, i);
                    setEnVuelo(null); setDestino(null);
                  }}
                  onDragEnd={() => { setEnVuelo(null); setDestino(null); }}
                >
                  {/* El número y el hilo que los une. El hilo SOLO en modo «uno
                      tras otro»: en paralelo no hay secuencia que seguir, y
                      dibujarla diría algo que no es verdad. */}
                  <div style={S.carril}>
                    <div style={S.numero(destino === i && enVuelo !== null)}>
                      {modo === 'secuencial' ? i + 1 : '•'}
                    </div>
                    {modo === 'secuencial' && i < elegidos.length - 1 && <div style={S.hilo} />}
                  </div>

                  <div style={S.tarjetaFirmante(false)}>
                    <div style={S.avatar} aria-hidden="true">{iniciales(quien.nombre, quien.correo)}</div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={S.nombreFirmante}>
                        {quien.nombre || quien.correo}
                        {/* Se marca a quien no salió del directorio. Un correo
                            mal tecleado se ve igual que uno bueno, y esta es la
                            última pantalla antes de que salga: mandar a firmar
                            no se deshace. */}
                        {esDeFuera(quien) && <span style={S.chipFuera}>de fuera</span>}
                      </div>
                      <div style={S.correoFirmante}>{quien.correo}</div>
                    </div>

                    {/* Con las flechas también: reordenar no puede depender de
                        poder arrastrar. */}
                    <button
                      type="button"
                      style={S.iconoFila}
                      aria-label={`Mover a ${quien.nombre || quien.correo}`}
                      title="Arrastra, o usa las flechas arriba y abajo"
                      onKeyDown={(ev) => {
                        if (ev.key !== 'ArrowUp' && ev.key !== 'ArrowDown') return;
                        ev.preventDefault();
                        mover(i, i + (ev.key === 'ArrowUp' ? -1 : 1));
                      }}
                    >
                      ⠿
                    </button>
                    <button
                      type="button"
                      style={S.iconoFila}
                      title={`Quitar a ${quien.nombre || quien.correo}`}
                      aria-label={`Quitar a ${quien.nombre || quien.correo}`}
                      onClick={() => quitar(quien.correo)}
                    >
                      🗑
                    </button>
                  </div>
                </div>
              ))}

              {/* Añadir, también a quien no esté en el directorio.
                  A una reunión va gente de fuera —un cliente, un notario, el
                  consejero sin cuenta en la empresa— y firman el acta igual que
                  los demás. El servidor nunca lo impidió: guarda nombre y correo
                  y solo exige que el correo tenga forma de correo. La puerta
                  estaba cerrada solo aquí.

                  `interpretarFirmante` es lo que distingue este caso del de
                  Planner: allí un externo se apunta con el nombre y ya está;
                  aquí hace falta el correo, porque el correo ES la petición. */}
              <div style={S.filaFirmante(false)}>
                <div style={S.carril}>
                  <div style={S.numero(false)}>
                    {modo === 'secuencial' ? elegidos.length + 1 : '+'}
                  </div>
                </div>
                <div style={{ ...S.tarjetaFirmante(true), display: 'block', padding: '8px 10px' }}>
                  <SelectorDePersona
                    personas={gente}
                    valor=""
                    textoMostrado=""
                    textoLibre
                    interpretarTexto={interpretarFirmante}
                    vacio="Agregar firmante"
                    etiqueta="Añadir un firmante, del directorio o de fuera"
                    onCambio={(id, persona) => añadir(persona)}
                  />
                </div>
              </div>

              <p style={S.ojo}>
                Escribe un nombre para buscarlo, o un correo para invitar a alguien de fuera
                de la organización.
              </p>
            </div>

            {/* ── El recado ───────────────────────────────────────────
                Va con la ronda y sale en el correo de cada firmante,
                atribuido a quien lo escribe. Sin este hueco, el contexto
                —«esta es la versión corregida», «fíjate en el punto 4»—
                viajaba en un correo aparte, separado del documento al que se
                refiere, y quien firma tenía que juntarlos. */}
            <div style={S.rotulo}>
              Mensaje <span style={{ fontWeight: 400, color: C.tenue }}>(opcional)</span>
            </div>
            <div>
              <textarea
                style={S.area}
                value={mensaje}
                maxLength={MAX_MENSAJE}
                onChange={(e) => setMensaje(e.target.value.slice(0, MAX_MENSAJE))}
                placeholder="Escribe un mensaje para los firmantes…"
                aria-label="Mensaje para los firmantes"
              />
              <div style={S.contador(mensaje.length > MAX_MENSAJE - 60)}>
                {mensaje.length}/{MAX_MENSAJE}
              </div>
            </div>

            {aviso && (
              <div style={{ ...S.recuadro, borderLeftColor: malo ? C.error : C.atencion }}>
                {aviso}
              </div>
            )}
          </div>
        </div>

        <div style={S.pie}>
          <span style={S.estado(false)}>
            {esperando ? 'Hay una ronda en curso: al mandar otra, se cancela.' : ''}
          </span>
          <button style={S.botonFlojo} onClick={() => onCerrar(false)}>Cancelar</button>
          <button
            style={S.botonFuerte(elegidos.length === 0 || mandando || sinDocumento || !hayBuzon)}
            disabled={elegidos.length === 0 || mandando || sinDocumento || !hayBuzon}
            onClick={enviar}
          >
            <span aria-hidden="true">➤</span>
            {mandando
              ? (paso || 'Mandando…')
              : elegidos.length === 1 ? 'Enviar a 1 firmante' : `Enviar a ${elegidos.length} firmantes`}
          </button>
        </div>
      </div>
    </div>

    {colocando && bytesDelPdf && (
      <div
        style={S.fondoVisor}
        onMouseDown={(e) => { if (e.target === e.currentTarget) setColocando(false); }}
      >
        <div style={S.cajaVisor} role="dialog" aria-modal="true" aria-label="Dónde firma cada quien">
          <div style={{ ...S.cabecera, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <h3 style={S.titulo}>Dónde firma cada quien</h3>
              <p style={{ ...S.bajada, margin: '2px 0 0' }}>
                {titulo || 'El acta'}{folio ? ` · ${folio}` : ''}
              </p>
            </div>
            <button style={S.botonChico} onClick={() => setColocando(false)}>Listo</button>
          </div>
          <div style={S.cuerpoVisor}>

            {/* A quién le toca el siguiente clic. Sin esto habría que adivinar
                de quién es el recuadro que se acaba de poner. */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '6px 0 10px' }}>
              {elegidos.map((p) => {
                const suyos = recuadros.filter(c => c.correo === p.correo).length;
                const activo = p.correo === turnoDe;
                return (
                  <button
                    key={p.correo}
                    onClick={() => setTurnoDe(p.correo)}
                    style={{
                      ...S.botonChico,
                      background: activo ? C.acento : C.panel,
                      color: activo ? C.acentoTexto : C.texto,
                      borderColor: activo ? C.acento : C.borde
                    }}
                    title={p.correo}
                  >
                    {p.nombre || p.correo}
                    {suyos > 0 && ` · ${suyos}`}
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              <button style={S.botonChico} onClick={ponerEnTodas} disabled={!turnoDe}>
                Repetir su firma en todas las hojas
              </button>
              <button
                style={S.botonChico}
                onClick={() => setRecuadros(previos => previos.filter(c => c.correo !== turnoDe))}
                disabled={!recuadros.some(c => c.correo === turnoDe)}
              >
                Quitar las suyas
              </button>
            </div>

            <VisorDePdf
              datos={bytesDelPdf}
              colocando={Boolean(turnoDe)}
              alAbrir={setHojas}
              alColocar={ponerRecuadro}
              alQuitar={quitarRecuadro}
              alMover={moverRecuadro}
              alto="calc(94vh - 300px)"
              recuadros={recuadros.map(c => ({
                ...c,
                mio: c.correo === turnoDe,
                nombre: (elegidos.find(p => p.correo === c.correo) || {}).nombre || c.correo
              }))}
              pista={turnoDe
                ? 'Pulsa en el documento para poner la firma de quien esté marcado arriba. '
                  + 'Los recuadros se arrastran, se agrandan por la esquina y se quitan con la ×.'
                : 'Elige arriba de quién vas a colocar la firma.'}
            />

            {/* Las dos cosas que salen mal y no se ven en pantalla. */}
            {faltanPorColocar.length > 0 && (
              <div style={{ ...S.recuadro, borderLeftColor: C.atencion, marginTop: 10 }}>
                Sin recuadro, y su firma irá a la tabla de asistentes del Word en vez de a
                una hoja concreta: {faltanPorColocar.map(p => p.nombre || p.correo).join(', ')}.
              </div>
            )}
            {sePisanRecuadros.length > 0 && (
              <div style={{ ...S.recuadro, borderLeftColor: C.atencion, marginTop: 10 }}>
                Hay {sePisanRecuadros.length === 1 ? 'una firma' : `${sePisanRecuadros.length} firmas`}
                {' '}puesta encima de otra. En pantalla se ven como una sola, y en el PDF final
                las dos rúbricas saldrían superpuestas e ilegibles.
              </div>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default EnviarAFirmas;
